import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import {
  CriticalPointAiPopoverState,
  CriticalPointAiPopoverStateService,
} from "src/app/services/critical-point-ai-popover-state.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-critical-point-ai-popover",
  templateUrl: "./critical-point-ai-popover.component.html",
  styleUrls: ["./critical-point-ai-popover.component.scss"],
})
export class CriticalPointAiPopoverComponent implements OnInit, OnDestroy {
  @Input()
  text: string = "";

  @Input()
  getImage?: () => Promise<string | undefined>;

  @Output()
  close = new EventEmitter<void>();

  @Output()
  accept = new EventEmitter<string>();

  public readonly descriptionControl = new FormControl<string>("", {
    nonNullable: true,
  });

  private readonly subscriptions: Subscription[] = [];
  private isSynchronizingDescriptionControl = false;

  constructor(public stateService: CriticalPointAiPopoverStateService) {}

  ngOnInit(): void {
    // Initialize state if needed
    if (!this.stateService.hasState()) {
      this.stateService.initializeState(this.text ?? "");
    }

    // Sync form control with service state
    this.syncFormControlFromState();

    // Subscribe to state changes
    this.subscriptions.push(
      this.stateService.state$.subscribe((state) => {
        if (state) {
          this.syncFormControlFromState();
        }
      })
    );

    // Subscribe to form control changes
    this.subscriptions.push(
      this.descriptionControl.valueChanges.subscribe((value) => {
        if (this.isSynchronizingDescriptionControl || typeof value !== "string") {
          return;
        }
        this.stateService.updateCurrentDescription(value);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  get state(): CriticalPointAiPopoverState | null {
    return this.stateService.getState();
  }

  get isLoading(): boolean {
    return this.stateService.isLoading();
  }

  get isRewriting(): boolean {
    return this.state?.isRewriting ?? false;
  }

  get generatedDescriptions(): string[] {
    return this.state?.generatedDescriptions ?? [];
  }

  get currentDescriptionIndex(): number {
    return this.state?.currentDescriptionIndex ?? 0;
  }

  public showPreviousDescription(): void {
    this.stateService.showPreviousDescription();
  }

  public showNextDescription(): void {
    this.stateService.showNextDescription();
  }

  public onCloseClick(): void {
    this.close.emit();
  }

  public onAcceptClick(): void {
    if (this.isLoading) {
      return;
    }

    const value = this.descriptionControl.value;
    this.stateService.clearState();
    this.accept.emit(value);
  }

  public onRewriteClick(): void {
    if (this.isLoading) {
      return;
    }

    const baseDescription = (this.state?.baseDescription ?? "").trim();
    if (!baseDescription) {
      return;
    }

    this.stateService.rewriteDescription(this.getImage);
  }

  private syncFormControlFromState(): void {
    const currentDescription = this.stateService.getCurrentDescription();
    if (this.descriptionControl.value !== currentDescription) {
      this.isSynchronizingDescriptionControl = true;
      this.descriptionControl.setValue(currentDescription);
      this.isSynchronizingDescriptionControl = false;
    }
  }
}
